import React, { useEffect, useMemo, useRef, useState } from "react";
import { Home as HomeIcon } from "lucide-react";
import { useNavigate } from "react-router-dom";

import RouteFallback from "@/sot/primitives/RouteFallback.jsx";
import { useLanguage } from "@/contexts/LanguageContext.jsx";
import Button from "@/components/ui/Button";
import { getMedicalInstitutionFacilityLogoUrl, loadMedicalInstitutionNetwork } from "./institutionService.js";
import { PageShell } from "./InstitutionsPage.jsx";
import {
  INSTITUTION_HOME_HISTORY_CHANGED,
  readInstitutionHomeHistory,
  recordInstitutionHomeFacilityView,
  recordInstitutionHomeMemberView,
  rehydrateInstitutionHomeHistory,
  writeInstitutionHomeHistory,
} from "./institutionHomeHistory.js";

function text(value) {
  return String(value || "").trim();
}

function facilityName(facility, fallback) {
  return text(facility?.name || facility?.facility_name || facility?.title) || fallback;
}

function facilityType(facility, fallback) {
  return text(facility?.type || facility?.facility_type) || fallback;
}

function memberName(member, fallback) {
  return text(member?.fullName || member?.full_name || member?.name || member?.display_name) || fallback;
}

function memberRole(member, fallback) {
  return text(member?.customRoleLabel || member?.role).replaceAll("_", " ") || fallback;
}

function facilityPath(parentInstitutionId, facilityId) {
  return `/institutions/medical-institutions/${encodeURIComponent(facilityId)}?institution=${encodeURIComponent(parentInstitutionId)}`;
}

function memberPath(parentInstitutionId, membershipId) {
  const params = new URLSearchParams({ institution: parentInstitutionId, member: membershipId });
  return `/institutions/members?${params.toString()}`;
}

function useRailOverflow(ref, itemCount) {
  const [overflowing, setOverflowing] = useState(false);
  useEffect(() => {
    const rail = ref.current;
    if (!rail) return undefined;
    const measure = () => setOverflowing(rail.scrollWidth > rail.clientWidth + 1);
    measure();
    const observer = typeof ResizeObserver === "undefined" ? null : new ResizeObserver(measure);
    observer?.observe(rail);
    window.addEventListener("resize", measure);
    return () => {
      observer?.disconnect();
      window.removeEventListener("resize", measure);
    };
  }, [itemCount, ref]);
  return overflowing;
}

const HOME_INSTITUTION_AVATAR_FALLBACK = "/icons/medical-institution/hospital_avatar.svg";

function InstitutionCard({ facility, onVisit, logoUrl = "", labels }) {
  return (
    <article className="institution-home-card institution-home-card--facility">
      <div className="institution-home-card__content">
        <span className="institution-home-card__image" aria-hidden="true">
          <img className={logoUrl ? "" : "is-placeholder"} src={logoUrl || HOME_INSTITUTION_AVATAR_FALLBACK} alt="" />
        </span>
        <div className="institution-home-card__copy">
          <div>
            <h3>{facilityName(facility, labels.institution)}</h3>
            <p>{facilityType(facility, labels.institution)}</p>
          </div>
          <button type="button" onClick={onVisit}>{labels.visit}</button>
        </div>
      </div>
    </article>
  );
}

function MemberCard({ member, onVisit, labels }) {
  return (
    <article className="institution-home-card institution-home-card--member">
      <div className="institution-home-card__content">
        <div className="institution-home-card__copy">
          <div>
            <h3>{memberName(member, labels.member)}</h3>
            <p>{memberRole(member, labels.member)}</p>
          </div>
          <button type="button" onClick={onVisit}>{labels.visit}</button>
        </div>
      </div>
    </article>
  );
}

function TruthfulEmptyState({ body, actionLabel, onAction }) {
  return (
    <div className="institution-home-empty-state">
      <p>{body}</p>
      <Button type="button" variant="tonal" onClick={onAction}>{actionLabel}</Button>
    </div>
  );
}

function scrollRailWithWheel(event) {
  const rail = event.currentTarget;
  if (rail.scrollWidth <= rail.clientWidth || (!event.deltaX && !event.deltaY)) return;
  event.preventDefault();
  rail.scrollLeft += event.deltaX || event.deltaY;
}

function HomeSection({ title, children, empty, railRef, overflowing }) {
  return (
    <section className="institution-home-section" aria-labelledby={`institution-home-${title.replaceAll(" ", "-").toLowerCase()}`}>
      <h2 id={`institution-home-${title.replaceAll(" ", "-").toLowerCase()}`}>{title}</h2>
      {empty ? <div className="institution-home-empty">{empty}</div> : (
        <div className="institution-home-rail-wrap">
          <div ref={railRef} className="institution-home-rail" role="list" tabIndex={0} onWheel={scrollRailWithWheel}>{children}</div>
          {overflowing ? <span className="institution-home-rail-fade" aria-hidden="true" /> : null}
        </div>
      )}
    </section>
  );
}

export default function InstitutionHome({ workspace, onInstitutionSelect }) {
  const { t } = useLanguage();
  const navigate = useNavigate();
  const userId = text(workspace?.user?.id);
  const institutionId = text(workspace?.activeInstitution?.id);
  const [history, setHistory] = useState(() => readInstitutionHomeHistory(userId));
  const [network, setNetwork] = useState(null);
  const [loadError, setLoadError] = useState(false);
  const [logoUrls, setLogoUrls] = useState({});
  const institutionRailRef = useRef(null);
  const memberRailRef = useRef(null);

  useEffect(() => setHistory(readInstitutionHomeHistory(userId)), [userId]);
  useEffect(() => {
    const onHistoryChanged = (event) => {
      if (!event?.detail?.userId || event.detail.userId === userId) setHistory(readInstitutionHomeHistory(userId));
    };
    window.addEventListener(INSTITUTION_HOME_HISTORY_CHANGED, onHistoryChanged);
    return () => window.removeEventListener(INSTITUTION_HOME_HISTORY_CHANGED, onHistoryChanged);
  }, [userId]);
  useEffect(() => {
    if (!institutionId) return undefined;
    let active = true;
    setNetwork(null);
    setLoadError(false);
    loadMedicalInstitutionNetwork({ institutionId })
      .then((next) => { if (active) setNetwork(next); })
      .catch(() => { if (active) setLoadError(true); });
    return () => { active = false; };
  }, [institutionId]);

  const facilities = network?.pendingMigration ? [] : network?.facilities || [];
  // Home recency deliberately represents general parent-Institution Members
  // only. Facility membership search and Surveillance authority are separate
  // products and cannot populate this rail.
  const members = useMemo(() => (workspace?.members || []).filter((member) => {
    const status = text(member?.status || "active").toLowerCase();
    const parentId = text(member?.institution_id || member?.institutionId || institutionId);
    return status === "active" && parentId === institutionId;
  }), [institutionId, workspace?.members]);
  const recent = useMemo(
    () => rehydrateInstitutionHomeHistory(history, { parentInstitutionId: institutionId, facilities, members }),
    [facilities, history, institutionId, members],
  );
  const institutionRailOverflow = useRailOverflow(institutionRailRef, recent.institutions.length);
  const memberRailOverflow = useRailOverflow(memberRailRef, recent.members.length);
  const mohSetup = workspace?.mohSetup?.canResume ? workspace.mohSetup : null;

  useEffect(() => {
    if (!userId || !network || network.pendingMigration) return;
    const before = JSON.stringify(readInstitutionHomeHistory(userId));
    const next = JSON.stringify(recent.pruned);
    if (before !== next) writeInstitutionHomeHistory(userId, recent.pruned);
  }, [network, recent.pruned, userId]);
  useEffect(() => {
    let active = true;
    const missing = recent.institutions.filter(({ facility }) => facility?.logoPath && !logoUrls[facility.id]);
    if (!missing.length) return undefined;
    Promise.all(missing.map(async ({ facility }) => [facility.id, await getMedicalInstitutionFacilityLogoUrl(facility.logoPath)]))
      .then((resolved) => {
        if (!active) return;
        setLogoUrls((current) => ({ ...current, ...Object.fromEntries(resolved.filter(([, value]) => value)) }));
      })
      .catch(() => undefined);
    return () => { active = false; };
  }, [logoUrls, recent.institutions]);
  const visitFacility = (facility) => {
    const facilityId = text(facility?.id);
    if (!institutionId || !facilityId) return;
    recordInstitutionHomeFacilityView(userId, { parentInstitutionId: institutionId, facilityId });
    navigate(facilityPath(institutionId, facilityId));
  };
  const visitMember = (entry, member) => {
    if (!institutionId || !member) return;
    const membershipId = text(member.membershipId || member.id || entry?.membershipId);
    if (!membershipId && !text(member.userId || entry?.userId)) return;
    recordInstitutionHomeMemberView(userId, {
      parentInstitutionId: institutionId,
      facilityId: null,
      membershipId,
        userId: member.userId || member.user_id || entry?.userId,
    });
    navigate(memberPath(institutionId, membershipId));
  };

  return (
    <PageShell workspace={workspace} title={t("institutionHome.title", "Home")} icon={HomeIcon} dashboardHeader onInstitutionSelect={onInstitutionSelect} pageTopBarTitle={t("institutionHome.title", "Home")} contentClassName="afrimed-rail afrimed-rail--comfortable institution-home-content">
      <main className="institution-home" aria-label={t("institutionHome.title", "Home")}>
        {mohSetup && mohSetup.setupStatus !== "complete" ? <section className="institution-home-setup-status" aria-label={t("institutionHome.setupStatusTitle", "MoH workspace setup")}>
          <div><h2>{t("institutionHome.setupStatusTitle", "MoH workspace setup")}</h2><p>{t("institutionHome.setupStatusBody", "Your authorised Institution is ready to explore. Complete facilities and roster intake when you are ready.")}</p></div>
          <Button type="button" variant="tonal" onClick={() => navigate(`/onboarding/moh?resume=1&institution=${encodeURIComponent(institutionId)}`)}>{t("institutionHome.completeSetup", "Complete setup")}</Button>
        </section> : null}
        {!network && !loadError ? <div className="institution-home-loading"><RouteFallback label={t("institutionHome.loading", "Loading Institution Home…")} /></div> : null}
        {loadError ? <p className="institution-home-empty" role="alert">{t("institutionHome.loadError", "Institution history could not be loaded. Try again from an authorised Institution workspace.")}</p> : null}
        {network?.pendingMigration ? <p className="institution-home-empty" role="status">{t("institutionHome.unavailable", "Institution Home is unavailable until the authorised workspace can be read.")}</p> : null}
        {network && !network.pendingMigration ? <>
          <HomeSection title={t("institutionHome.recentInstitutions", "Recently viewed institutions")} empty={recent.institutions.length ? null : <TruthfulEmptyState body={t("institutionHome.emptyInstitutions", "Opened authorised Medical Institutions appear here.")} actionLabel={t("institutionHome.browseInstitutions", "Browse authorised Medical Institutions")} onAction={() => navigate(`/institutions/medical-institutions?institution=${encodeURIComponent(institutionId)}`)} />} railRef={institutionRailRef} overflowing={institutionRailOverflow}>
            {recent.institutions.map(({ entry, facility }) => <div role="listitem" key={`${entry.parentInstitutionId}:${entry.facilityId}`}><InstitutionCard facility={facility} labels={{ visit: t("institutionHome.visit", "Visit"), institution: t("institutionHome.institutionFallback", "Medical Institution") }} logoUrl={logoUrls[facility.id]} onVisit={() => visitFacility(facility)} /></div>)}
          </HomeSection>
          <HomeSection title={t("institutionHome.recentMembers", "Recently viewed members")} empty={recent.members.length ? null : <TruthfulEmptyState body={t("institutionHome.emptyMembers", "Explicitly opened authorised Members appear here.")} actionLabel={t("institutionHome.openMembers", "Open Institution Members")} onAction={() => navigate(`/institutions/members?institution=${encodeURIComponent(institutionId)}`)} />} railRef={memberRailRef} overflowing={memberRailOverflow}>
            {recent.members.map(({ entry, member }) => <div role="listitem" key={`${entry.parentInstitutionId}:${entry.facilityId || "parent"}:${entry.membershipId || entry.userId}`}><MemberCard member={member} labels={{ visit: t("institutionHome.visit", "Visit"), member: t("institutionHome.memberFallback", "Institution member") }} onVisit={() => visitMember(entry, member)} /></div>)}
          </HomeSection>
        </> : null}
      </main>
    </PageShell>
  );
}

export { facilityPath, memberPath, TruthfulEmptyState };
